/**
 * Stable mount policy for multi-layer PLATEAU Tokyo scenes.
 * Keeps basemap + ground context mounted; defers cache-heavy detail layers
 * until the camera is close enough, then keeps them mounted until the user
 * toggles them off in the layer panel.
 *
 * All keyword detection uses anchored regexes (`\b` or PLATEAU id-style
 * dashes/underscores). Earlier substring matches caused false positives
 * like "openstree**tmap**" being classified as a "tree" detail layer.
 */
import { PLATEAU_FEATURES } from './plateauFeatures';

/** Distance at which detail tilesets (roads, furniture, veg) may mount. */
export const PLATEAU_STREET_DETAIL_DISTANCE_M = 1000;

/** Pull back past this distance before detail tilesets stop unlocking (not unmounting). */
export const PLATEAU_STREET_DETAIL_RELEASE_DISTANCE_M = 1500;

/** Word-boundary check: surrounding chars must not be letters/digits. */
function hasToken(text: string, token: string): boolean {
  return new RegExp(`(^|[^a-z0-9])${token}([^a-z0-9]|$)`, 'i').test(text);
}

/** Match any of the tokens via word boundaries. */
function hasAnyToken(text: string, tokens: readonly string[]): boolean {
  return tokens.some((token) => hasToken(text, token));
}

const PRIMARY_BUILDING_TOKENS = ['bldg', 'building'] as const;
const PRIMARY_BRIDGE_TOKENS = ['brid', 'bridge'] as const;
const GROUND_TOKENS = [
  'ground', 'landcover', 'dem', 'relief', 'lsus', 'slope', 'tilt', 'soil', 'landuse',
] as const;
const ROAD_TOKENS = ['tran', 'road'] as const;
const FURNITURE_TOKENS = ['frn', 'furniture'] as const;
const VEG_TOKENS = ['veg', 'plantcover', 'tree', 'trees', 'solitary'] as const;
const DETAIL_OTHER_TOKENS = ['flood', 'htd', 'hazard', 'ubld'] as const;
const BUILDING_LIKE_TOKENS = [...PRIMARY_BUILDING_TOKENS, 'ubld'] as const;

/** Human-readable camera-to-target distance for the zoom-gate chip. */
export function formatPlateauCameraDistanceM(distanceM: number | null): string {
  if (distanceM == null || !Number.isFinite(distanceM)) return '—';
  if (distanceM >= 10_000) return `${(distanceM / 1000).toFixed(0)} km`;
  if (distanceM >= 1000) return `${(distanceM / 1000).toFixed(1)} km`;
  return `${Math.round(distanceM)} m`;
}

/** Basemap / terrain backdrop layers — always mount first. */
export function isPlateauBasemapLayer(layer: {
  id: string;
  kind: string;
  style?: unknown;
}): boolean {
  if (layer.kind !== 'raster' && layer.kind !== 'terrain') return false;
  const style = layer.style as { family?: string; isSyntheticBasemap?: boolean; gtwinSlot?: string } | undefined;
  if (style?.isSyntheticBasemap === true) return true;
  if (style?.family === 'basemap') return true;
  if (style?.gtwinSlot === 'basemaps') return true;
  return layer.id.toLowerCase().startsWith('layer:basemap:');
}

export function isPlateauPrimaryLayer(layerId: string, layerLabel?: string): boolean {
  const text = `${layerId} ${layerLabel ?? ''}`.toLowerCase();
  if (
    hasAnyToken(text, PRIMARY_BUILDING_TOKENS)
    && hasToken(text, 'lod2')
    && !hasToken(text, 'lod1')
    && !/no.?texture|no_texture/.test(text)
  ) {
    return true;
  }
  if (hasAnyToken(text, PRIMARY_BRIDGE_TOKENS) && hasToken(text, 'lod2')) return true;
  return false;
}

/** Ground / land-surface tilesets (DEM, landcover, relief). */
export function isPlateauGroundLayer(layerId: string, layerLabel?: string): boolean {
  if (layerId.toLowerCase().startsWith('layer:basemap:')) return false;
  const text = `${layerId} ${layerLabel ?? ''}`.toLowerCase();
  return hasAnyToken(text, GROUND_TOKENS);
}

/** Cache-heavy layers deferred until street-level zoom. */
export function isPlateauDeferrableDetailLayer(
  layerId: string,
  layerLabel?: string,
  layerStyle?: unknown,
  layerKind?: string,
): boolean {
  // Hard exclusion: 2D map-layer kinds (TiTiler COG rasters, terrain
  // ColorLayers) are cheap to keep mounted and are often the ONLY content of
  // a DEM/ortho project. The street-detail deferral exists for cache-heavy
  // 3D tilesets; without this guard a terrain COG whose id contains "dem"
  // (e.g. converter-file-533946_dem_v2.cog) matched the GROUND token list,
  // was deferred behind the 1000 m street-detail gate, and the project
  // rendered nothing but the basemap.
  if (layerKind === 'raster' || layerKind === 'terrain') return false;
  // Hard exclusion: basemaps are never deferrable. Old substring matching
  // caught "tree" inside "open**stree**tmap" and dropped the OSM basemap.
  if (
    isPlateauBasemapLayer({ id: layerId, kind: 'raster', style: layerStyle })
    || layerId.toLowerCase().startsWith('layer:basemap:')
  ) {
    return false;
  }
  if (isPlateauPrimaryLayer(layerId, layerLabel)) return false;
  if (isPlateauGroundLayer(layerId, layerLabel)) return true;
  const text = `${layerId} ${layerLabel ?? ''}`.toLowerCase();
  if (hasAnyToken(text, ROAD_TOKENS)) return true;
  if (hasAnyToken(text, FURNITURE_TOKENS)) return true;
  if (hasAnyToken(text, VEG_TOKENS)) return true;
  if (hasAnyToken(text, DETAIL_OTHER_TOKENS)) return true;
  // Untextured / LOD1 / LOD3 / LOD4 of any feature class is deferrable.
  if (hasToken(text, 'lod1') || hasToken(text, 'lod3') || hasToken(text, 'lod4')) {
    return true;
  }
  return false;
}

/**
 * Layers that stay mounted once loaded until the user hides them in the panel.
 * Basemap, roads, furniture, and ground surfaces — not vegetation or buildings.
 */
export function isPlateauStickyMountLayer(layer: {
  id: string;
  label?: string;
  kind: string;
  style?: unknown;
}): boolean {
  if (isPlateauBasemapLayer(layer)) return true;
  const text = `${layer.id} ${layer.label ?? ''}`.toLowerCase();
  if (hasAnyToken(text, ROAD_TOKENS) || hasAnyToken(text, FURNITURE_TOKENS)) return true;
  return isPlateauGroundLayer(layer.id, layer.label);
}

export function nextPlateauStreetDetailState(
  currentlyAllowed: boolean,
  cameraDistanceM: number | null,
): boolean {
  if (!Number.isFinite(cameraDistanceM)) return false;
  const dist = cameraDistanceM as number;
  if (currentlyAllowed) {
    return dist <= PLATEAU_STREET_DETAIL_RELEASE_DISTANCE_M;
  }
  return dist <= PLATEAU_STREET_DETAIL_DISTANCE_M;
}

export function filterPlateauMountLayers<T extends { id: string; label?: string; style?: unknown; kind?: string }>(
  layers: readonly T[],
  allowDetailLayers: boolean,
): T[] {
  if (!PLATEAU_FEATURES.stableMountPolicy || allowDetailLayers) {
    return [...layers];
  }
  return layers.filter(
    (layer) => !isPlateauDeferrableDetailLayer(layer.id, layer.label, layer.style, layer.kind),
  );
}

/**
 * Stagger priority for detail layers — lower numbers unlock first.
 * 1 roads → 2 furniture → 3 ground → 4 vegetation → 5 bridges → 99 rest.
 */
export function getDetailLayerPriority(layerId: string, layerLabel?: string): number {
  const text = `${layerId} ${layerLabel ?? ''}`.toLowerCase();
  if (hasAnyToken(text, ROAD_TOKENS)) return 1;
  if (hasAnyToken(text, FURNITURE_TOKENS)) return 2;
  if (hasAnyToken(text, GROUND_TOKENS)) return 3;
  if (hasAnyToken(text, VEG_TOKENS)) return 4;
  if (hasAnyToken(text, PRIMARY_BRIDGE_TOKENS)) return 5;
  return 99;
}

/** Building LOD rank for mount order — lower LOD mounts before higher LOD. */
export function getBuildingLodMountRank(layerId: string, layerLabel?: string): number {
  const text = `${layerId} ${layerLabel ?? ''}`.toLowerCase();
  if (!hasAnyToken(text, BUILDING_LIKE_TOKENS)) return 99;
  if (hasToken(text, 'lod1') || /no.?texture|no_texture/.test(text)) return 1;
  if (hasToken(text, 'lod3') || hasToken(text, 'lod4')) return 3;
  return 2;
}

/**
 * Global mount tier for addLayer ordering (lower = earlier).
 * Basemap → roads → furniture → ground → vegetation → buildings (LOD1→LOD2→LOD3) → bridges → other.
 */
export function getPlateauMountTier(layer: {
  id: string;
  label?: string;
  kind: string;
  style?: unknown;
}): number {
  if (isPlateauBasemapLayer(layer)) return 0;
  const text = `${layer.id} ${layer.label ?? ''}`.toLowerCase();
  if (hasAnyToken(text, ROAD_TOKENS)) return 10;
  if (hasAnyToken(text, FURNITURE_TOKENS)) return 20;
  if (isPlateauGroundLayer(layer.id, layer.label)) return 30;
  if (hasAnyToken(text, VEG_TOKENS)) return 40;
  if (hasAnyToken(text, BUILDING_LIKE_TOKENS)) {
    return 50 + getBuildingLodMountRank(layer.id, layer.label);
  }
  if (hasAnyToken(text, PRIMARY_BRIDGE_TOKENS)) return 80;
  return 90;
}

export function comparePlateauLayerMountOrder(
  a: { id: string; label?: string; kind: string; style?: unknown },
  b: { id: string; label?: string; kind: string; style?: unknown },
): number {
  const tierDiff = getPlateauMountTier(a) - getPlateauMountTier(b);
  if (tierDiff !== 0) return tierDiff;
  return a.id.localeCompare(b.id);
}

/** Split mount list into basemap (phase 1) and everything else (phase 2+). */
export function partitionPlateauMountPhases<T extends { id: string; label?: string; kind: string; style?: unknown }>(
  layers: readonly T[],
): { basemap: T[]; scene: T[] } {
  const sorted = [...layers].sort(comparePlateauLayerMountOrder);
  const basemap = sorted.filter((layer) => isPlateauBasemapLayer(layer));
  const scene = sorted.filter((layer) => !isPlateauBasemapLayer(layer));
  return { basemap, scene };
}

/**
 * Stage non-basemap layers for sequential mount:
 * roads/furniture → ground → buildings (LOD order) → bridges/other.
 */
export function partitionSceneMountStages<T extends { id: string; label?: string; kind: string; style?: unknown }>(
  layers: readonly T[],
): { roads: T[]; ground: T[]; buildings: T[]; other: T[] } {
  const sorted = [...layers].sort(comparePlateauLayerMountOrder);
  const roads: T[] = [];
  const ground: T[] = [];
  const buildings: T[] = [];
  const other: T[] = [];
  for (const layer of sorted) {
    const tier = getPlateauMountTier(layer);
    if (tier > 0 && tier <= 20) roads.push(layer);
    else if (tier === 30) ground.push(layer);
    else if (tier >= 50 && tier < 80) buildings.push(layer);
    else if (tier > 0) other.push(layer);
  }
  return { roads, ground, buildings, other };
}

/** Visible detail layers waiting for street-level zoom (stable mount policy). */
export function listPlateauDeferredDetailLayers<T extends { id: string; label?: string; style?: unknown; kind?: string }>(
  visibleLayers: readonly T[],
  mountedLayers: readonly T[],
): T[] {
  if (!PLATEAU_FEATURES.stableMountPolicy) return [];
  const mountedIds = new Set(mountedLayers.map((layer) => layer.id));
  return visibleLayers.filter(
    (layer) =>
      isPlateauDeferrableDetailLayer(layer.id, layer.label, layer.style, layer.kind) && !mountedIds.has(layer.id),
  );
}
